import type { BlockTransfer, GroupRowModel, TransferSegment, TransferSegmentPart } from '../types';

/**
 * Where each shown row draws a piece of a transfer's line: one line per transfer, from its
 * source's row to its destination's, so in a chain A → B → C the A → B line never reaches C's
 * row. A row takes part through any of its groups, and a transfer that stays on one row (a
 * same-pen continuation) draws no line. The lines of one operation share its date and run side
 * by side, ordered by where they go.
 */
export function transferSegments(
    rows: GroupRowModel[],
    transfers: BlockTransfer[]
): Map<number, TransferSegment[]> {
    const rowIndexOf = new Map<number, number>();
    rows.forEach((row, index) => {
        for (const member of row.members) rowIndexOf.set(member.groupId, index);
    });

    const byOperation = new Map<number, { transfer: BlockTransfer; from: number; to: number }[]>();
    for (const transfer of transfers) {
        const from = rowIndexOf.get(transfer.sourceId);
        const to = rowIndexOf.get(transfer.destinationId);
        if (from === undefined || to === undefined || from === to) continue;
        byOperation.set(transfer.operationId, [
            ...(byOperation.get(transfer.operationId) ?? []),
            { transfer, from, to },
        ]);
    }

    const segments = new Map<number, TransferSegment[]>();
    for (const lines of byOperation.values()) {
        lines.sort((a, b) => a.to - b.to || a.from - b.from);
        lines.forEach(({ transfer, from, to }, laneIndex) => {
            const lane = laneIndex - (lines.length - 1) / 2;
            const first = Math.min(from, to);
            const last = Math.max(from, to);
            for (let index = first; index <= last; index++) {
                const part: TransferSegmentPart =
                    index === first ? 'start' : index === last ? 'end' : 'middle';
                const { groupId } = rows[index];
                const segment: TransferSegment = {
                    key: transfer.key,
                    leftPct: transfer.leftPct,
                    part,
                    lane,
                    lanes: lines.length,
                    endpoint: index === to ? 'destination' : index === from ? 'source' : null,
                    transfer,
                };
                segments.set(groupId, [...(segments.get(groupId) ?? []), segment]);
            }
        });
    }
    return segments;
}
